import { useActions, useValues } from 'kea'

import { TaxonomicFilter } from 'lib/components/TaxonomicFilter/TaxonomicFilter'
import { TaxonomicFilterGroupType } from 'lib/components/TaxonomicFilter/types'
import { Popover } from 'lib/lemon-ui/Popover/Popover'
import { databaseTableListLogic } from 'scenes/data-management/database/databaseTableListLogic'
import { insightLogic } from 'scenes/insights/insightLogic'
import { insightVizDataLogic } from 'scenes/insights/insightVizDataLogic'

import { groupsModel } from '~/models/groupsModel'
import { isInsightVizNode, isRetentionQuery } from '~/queries/utils'

import { taxonomicBreakdownFilterLogic } from './taxonomicBreakdownFilterLogic'

type TaxonomicBreakdownPopoverProps = {
    open: boolean
    setOpen: (open: boolean) => void
    children: React.ReactElement
    taxonomicType?: TaxonomicFilterGroupType
    breakdownType?: string
    breakdownValue?: string | number | null
}

export const TaxonomicBreakdownPopover = ({
    open,
    setOpen,
    children,
    taxonomicType,
    breakdownType,
    breakdownValue,
}: TaxonomicBreakdownPopoverProps): JSX.Element => {
    const { insightProps } = useValues(insightLogic)
    const { allEventNames, query, hasDataWarehouseSeries } = useValues(insightVizDataLogic(insightProps))
    const { databaseLoading } = useValues(databaseTableListLogic)
    const { groupsTaxonomicTypes } = useValues(groupsModel)
    const { includeSessions, taxonomicBreakdownType } = useValues(taxonomicBreakdownFilterLogic)

    const { currentDataWarehouseSchemaColumns } = useValues(taxonomicBreakdownFilterLogic)
    const { addBreakdown, replaceBreakdown } = useActions(taxonomicBreakdownFilterLogic)

    let taxonomicGroupTypes: TaxonomicFilterGroupType[]
    if (hasDataWarehouseSeries) {
        taxonomicGroupTypes = [TaxonomicFilterGroupType.DataWarehouseProperties]
    } else if (taxonomicBreakdownType === TaxonomicFilterGroupType.CohortsWithAllUsers) {
        taxonomicGroupTypes = [TaxonomicFilterGroupType.CohortsWithAllUsers]
    } else if (isRetentionQuery(query) || (isInsightVizNode(query) && isRetentionQuery(query.source))) {
        taxonomicGroupTypes = [
            TaxonomicFilterGroupType.MCPProperties,
            TaxonomicFilterGroupType.EventProperties,
            TaxonomicFilterGroupType.PersonProperties,
            TaxonomicFilterGroupType.EventFeatureFlags,
            ...groupsTaxonomicTypes,
            TaxonomicFilterGroupType.CohortsWithAllUsers,
            TaxonomicFilterGroupType.HogQLExpression,
            TaxonomicFilterGroupType.DataWarehousePersonProperties,
        ]
    } else {
        taxonomicGroupTypes = [
            // Only materializes when the insight has $mcp_* series in scope, so breakdowns
            // by e.g. tool name or error state lead with the known MCP schema.
            TaxonomicFilterGroupType.MCPProperties,
            TaxonomicFilterGroupType.EventProperties,
            TaxonomicFilterGroupType.PersonProperties,
            TaxonomicFilterGroupType.EventFeatureFlags,
            TaxonomicFilterGroupType.EventMetadata,
            ...groupsTaxonomicTypes,
            TaxonomicFilterGroupType.CohortsWithAllUsers,
            ...(includeSessions ? [TaxonomicFilterGroupType.SessionProperties] : []),
            TaxonomicFilterGroupType.HogQLExpression,
            TaxonomicFilterGroupType.DataWarehouseProperties,
            TaxonomicFilterGroupType.DataWarehousePersonProperties,
        ]
    }

    return (
        <Popover
            style={{ minHeight: '200px' }}
            overlay={
                <TaxonomicFilter
                    groupType={taxonomicType}
                    value={breakdownValue}
                    hogQLExpressionShowBreakdownLabelHint
                    onChange={(taxonomicGroup, value) => {
                        if (breakdownValue && breakdownType) {
                            replaceBreakdown(
                                {
                                    value: breakdownValue,
                                    type: breakdownType,
                                },
                                {
                                    value,
                                    group: taxonomicGroup,
                                }
                            )
                        } else {
                            addBreakdown(value, taxonomicGroup)
                        }

                        setOpen(false)
                    }}
                    eventNames={allEventNames}
                    taxonomicGroupTypes={taxonomicGroupTypes}
                    schemaColumns={currentDataWarehouseSchemaColumns}
                    schemaColumnsLoading={hasDataWarehouseSeries && databaseLoading}
                />
            }
            visible={open}
            onClickOutside={() => setOpen(false)}
        >
            {children}
        </Popover>
    )
}
